// frontend/src/components/shared/AccountTemplateModal.jsx
// Sectioned "apply to all" with a saveable template — the book's
// AgentDefaultTemplate experience, generalized:
//   scope="customer" -> book's or an agent's default customer settings
//   scope="agent"    -> an agent's default sub-agent settings
import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../../api';
import { colors, fontFamily } from '../../theme';
import ToastHost, { useToast } from './Toast';
import ConfirmDialogHost, { useConfirm } from './ConfirmDialog';
import { GENERAL_PERMS, SITE_OPTIONS, LIMIT_TYPES } from '../book/AgentDefaultTemplateModal';
import { isFieldVisible } from '../agent/permissionRules';

// [templateKey, label] — customer-scope numeric fields.
const CUSTOMER_NUM_FIELDS = [
  ['weekly_limit', 'Weekly Limit'],
  ['min_bet', 'Min Bet'],
  ['max_bet', 'Max Bet'],
  ['parlay_min_bet', 'Parlay Min Bet'],
  ['parlay_max_bet', 'Parlay Max Bet'],
  ['live_min_bet', 'Live Min Bet'],
  ['live_max_bet', 'Live Max Bet'],
  ['casino_min_bet', 'Casino Min Bet'],
  ['casino_max_bet', 'Casino Max Bet'],
  ['casino_weekly_max_win', 'Casino Weekly Max Win'],
  ['casino_weekly_max_loss', 'Casino Weekly Max Loss'],
  ['free_play_loss_percentage', 'Free Play Loss Rebate (%)'],
  ['game_limit', 'Contest Limit'],
];
const CUSTOMER_TOGGLE_FIELDS = [
  ['casino_access', 'Casino Access'],
  ['is_read_only', 'Read-Only'],
];
const AGENT_NUM_FIELDS = [
  ['weekly_limit', 'Weekly Limit'],
  ['agent_cut', 'Agent Cut (%)'],
  ['min_bet', 'Min Bet'],
  ['max_bet', 'Max Bet'],
  ['parlay_min_bet', 'Parlay Min Bet'],
  ['parlay_max_bet', 'Parlay Max Bet'],
];

export default function AccountTemplateModal({ scope, targetLabel, targetCount, perms = null, onClose, onApplied }) {
  const { toast, showToast } = useToast();
  const { confirmState, confirm, resolveConfirm } = useConfirm();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [openSections, setOpenSections] = useState(new Set(['general']));
  // One-shot deltas — NOT part of the saved template.
  const [adj, setAdj] = useState({ balance_adjustment: '', balance_reason: '', free_play_adjustment: '', free_play_reason: '' });

  useEffect(() => {
    api.getAccountTemplate(scope)
      .then(res => setData(res.data || {}))
      .catch(err => setLoadError(err.response?.data?.error || 'Failed to load template'))
      .finally(() => setLoading(false));
  }, [scope]);

  const toggleSection = (key) => setOpenSections(prev => {
    const next = new Set(prev);
    next.has(key) ? next.delete(key) : next.add(key);
    return next;
  });

  const setNum = (field, v) => setData(prev => ({ ...prev, [field]: v === '' ? undefined : Number(v) }));
  const setBool = (field, v) => setData(prev => ({ ...prev, [field]: v }));
  const val = (field, fallback = '') => (data && data[field] !== undefined && data[field] !== null ? data[field] : fallback);

  // Agent editing a customer template only sees fields the book granted them.
  const showCustomerField = (field) => !perms || isFieldVisible(field, perms);
  const showAgentPerm = (field) => !perms || !!perms[field];
  const canAdjustBalance = scope === 'customer' && (!perms || !!perms.can_change_settle_figure);
  const canAdjustFreePlay = scope === 'customer' && (!perms || !!perms.can_adjust_free_play_balance);

  const adjustmentsPayload = () => {
    const out = {};
    if (canAdjustBalance && Number(adj.balance_adjustment)) {
      out.balance_adjustment = Number(adj.balance_adjustment);
      if (adj.balance_reason) out.balance_reason = adj.balance_reason;
    }
    if (canAdjustFreePlay && Number(adj.free_play_adjustment)) {
      out.free_play_adjustment = Number(adj.free_play_adjustment);
      if (adj.free_play_reason) out.free_play_reason = adj.free_play_reason;
    }
    return Object.keys(out).length ? out : null;
  };

  const cleanPayload = () => {
    const out = {};
    Object.entries(data || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      out[k] = v;
    });
    return out;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.updateAccountTemplate(scope, cleanPayload());
      setData(res.data || {});
      showToast('Template saved');
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to save template', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleApply = async () => {
    const adjustments = adjustmentsPayload();
    const hasFields = Object.keys(cleanPayload()).length > 0;
    let msg = hasFields
      ? `Save first, then overwrite these settings on all ${targetCount} ${targetLabel} below you?`
      : `Apply to all ${targetCount} ${targetLabel} below you?`;
    if (adjustments) {
      const parts = [];
      if (adjustments.balance_adjustment) parts.push(`${adjustments.balance_adjustment > 0 ? '+' : ''}$${adjustments.balance_adjustment} credit balance`);
      if (adjustments.free_play_adjustment) parts.push(`${adjustments.free_play_adjustment > 0 ? '+' : ''}$${adjustments.free_play_adjustment} free play`);
      msg += `\n\nAlso apply ${parts.join(' and ')} to every customer (once).`;
    }
    msg += '\n\nThis cannot be undone.';
    const ok = await confirm(msg);
    if (!ok) return;
    setApplying(true);
    try {
      await api.updateAccountTemplate(scope, cleanPayload());
      const res = await api.applyAccountTemplate(scope, adjustments);
      showToast(res.message || 'Applied');
      if (res.skipped && res.skipped.length) {
        console.warn('Apply skipped:', res.skipped);
        showToast(`${res.skipped.length} skipped — see console`, 'error');
      }
      onApplied();
      onClose();
    } catch (err) {
      showToast(err.response?.data?.error || 'Failed to apply template', 'error');
    } finally {
      setApplying(false);
    }
  };

  const overlayStyle = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' };
  const modalStyle = { background: colors.bgGradient, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, maxWidth: '56rem', width: '100%', maxHeight: '90vh', overflowY: 'auto', fontFamily };
  const sectionCard = { background: colors.sectionBg, border: `1px solid ${colors.goldMuted}`, borderRadius: 8, marginBottom: '1rem' };
  const sectionHeader = { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', background: 'none', border: 'none', cursor: 'pointer', color: colors.textPrimary, fontFamily, fontWeight: 600, fontSize: '1rem' };
  const labelStyle = { display: 'block', marginBottom: '0.3rem', color: colors.textMuted, fontSize: '0.85rem' };
  const inputStyle = { background: colors.cardBg, color: colors.textPrimary, border: `1px solid ${colors.goldMuted}`, borderRadius: 4, padding: '0.5rem 0.75rem', width: '100%', fontFamily, boxSizing: 'border-box' };
  const numInputStyle = { ...inputStyle, width: 90, padding: '0.3rem 0.5rem', fontSize: '0.8rem' };
  const checkboxRow = { display: 'flex', alignItems: 'flex-start', gap: '0.5rem', padding: '0.5rem 0', borderBottom: `1px solid ${colors.sectionBg}` };
  const saveButtonStyle = { background: colors.goldActiveBg, border: `1px solid ${colors.gold}`, color: colors.textPrimary, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily };
  const applyButtonStyle = { background: 'rgba(74,222,128,0.15)', border: `1px solid ${colors.odds}`, color: colors.odds, borderRadius: 6, padding: '0.6rem 1.5rem', cursor: 'pointer', fontWeight: 700, fontFamily };

  const permCheckbox = (field, label, hint) => (
    <label key={field} style={{ ...checkboxRow, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!val(field, false)} onChange={e => setBool(field, e.target.checked)} style={{ marginTop: 3, accentColor: colors.gold }} />
      <div>
        <div style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>{label}</div>
        <div style={{ color: colors.textMuted, fontSize: '0.78rem' }}>{hint}</div>
      </div>
    </label>
  );

  const title = scope === 'agent' ? 'Default Sub-Agent Template' : 'Default Customer Template';

  return (
    <div style={overlayStyle}>
      <ToastHost toast={toast} />
      <ConfirmDialogHost confirmState={confirmState} resolveConfirm={resolveConfirm} confirmLabel="Save & Apply to All" />
      <div className="scrollbar-hide" style={modalStyle}>
        <div style={{ padding: '1.25rem', borderBottom: `1px solid ${colors.goldMuted}`, position: 'sticky', top: 0, background: colors.bgGradient, zIndex: 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2 style={{ color: colors.textPrimary, margin: 0 }}>{title}</h2>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: colors.textPrimary, fontSize: '1.5rem', cursor: 'pointer' }}>×</button>
          </div>
          <p style={{ color: colors.textMuted, fontSize: '0.85rem', marginTop: '0.3rem' }}>
            Set the fields you want, Save to keep the template, then Apply to push it onto all {targetCount} {targetLabel} below you. Leave a field blank to leave it out. Enter 0 for no limit.
          </p>
        </div>

        <div style={{ padding: '1.25rem' }}>
          {loading ? (
            <p style={{ color: colors.textMuted }}>Loading template...</p>
          ) : loadError || !data ? (
            <p style={{ color: colors.negative }}>{loadError || 'Failed to load template'}</p>
          ) : (
            <>
              <div style={sectionCard}>
                <button onClick={() => toggleSection('general')} style={sectionHeader}>
                  <span>{scope === 'agent' ? 'Base Account' : 'Limits'}</span>
                  {openSections.has('general') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
                </button>
                {openSections.has('general') && (
                  <div style={{ padding: '0 1.25rem 1.25rem' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                      {(scope === 'agent' ? AGENT_NUM_FIELDS : CUSTOMER_NUM_FIELDS)
                        .filter(([f]) => scope === 'agent' || showCustomerField(f))
                        .map(([field, label]) => (
                          <div key={field}>
                            <label style={labelStyle}>{label}</label>
                            <input type="number" step="0.01" min="0" style={inputStyle}
                              value={val(field)} onChange={e => setNum(field, e.target.value)} />
                          </div>
                        ))}
                    </div>
                    {scope === 'customer' && CUSTOMER_TOGGLE_FIELDS.some(([f]) => showCustomerField(f)) && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem 1.5rem', marginTop: '1.25rem', paddingTop: '1rem', borderTop: `1px solid ${colors.goldMuted}` }}>
                        {CUSTOMER_TOGGLE_FIELDS.filter(([f]) => showCustomerField(f)).map(([field, label]) => (
                          <label key={field} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', color: colors.textPrimary, fontSize: '0.9rem', cursor: 'pointer' }}>
                            <input type="checkbox" checked={!!val(field, false)} onChange={e => setBool(field, e.target.checked)} />
                            {label}
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {scope === 'agent' && (
                <>
                  <div style={sectionCard}>
                    <button onClick={() => toggleSection('limits')} style={sectionHeader}>
                      <span>Limit Permissions</span>
                      {openSections.has('limits') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
                    </button>
                    {openSections.has('limits') && (
                      <div style={{ padding: '0 1.25rem 1.25rem' }}>
                        {LIMIT_TYPES.filter(([, toggleField]) => showAgentPerm(toggleField)).map(([key, toggleField, minField, maxField, label]) => (
                          <div key={key} style={{ padding: '0.6rem 0', borderBottom: `1px solid ${colors.sectionBg}` }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', marginBottom: '0.4rem' }}>
                              <input type="checkbox" checked={!!val(toggleField, false)} onChange={e => setBool(toggleField, e.target.checked)} style={{ accentColor: colors.gold }} />
                              <span style={{ color: colors.textPrimary, fontWeight: 600, fontSize: '0.9rem' }}>{label}</span>
                            </label>
                            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', marginLeft: '1.5rem' }}>
                              <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                                Min <input type="number" step="0.01" min="0" value={val(minField)} onChange={e => setNum(minField, e.target.value)} style={numInputStyle} />
                              </label>
                              <label style={{ color: colors.textPrimary, fontSize: '0.8rem' }}>
                                Max <input type="number" step="0.01" min="0" value={val(maxField)} onChange={e => setNum(maxField, e.target.value)} style={numInputStyle} />
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={sectionCard}>
                    <button onClick={() => toggleSection('perms')} style={sectionHeader}>
                      <span>Permissions</span>
                      {openSections.has('perms') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
                    </button>
                    {openSections.has('perms') && (
                      <div style={{ padding: '0 1.25rem 1.25rem' }}>
                        {[...GENERAL_PERMS, ...SITE_OPTIONS]
                          .filter(([field]) => showAgentPerm(field))
                          .map(([field, label, hint]) => permCheckbox(field, label, hint))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {scope === 'customer' && (canAdjustBalance || canAdjustFreePlay) && (
                <div style={sectionCard}>
                  <button onClick={() => toggleSection('adjust')} style={sectionHeader}>
                    <span>Balance Adjustments</span>
                    {openSections.has('adjust') ? <ChevronDown size={18} color={colors.gold} /> : <ChevronRight size={18} color={colors.gold} />}
                  </button>
                  {openSections.has('adjust') && (
                    <div style={{ padding: '0 1.25rem 1.25rem' }}>
                      <p style={{ color: colors.textFaint, fontSize: '0.78rem', marginTop: 0, marginBottom: '0.85rem' }}>
                        A one-time credit (positive) or debit (negative) posted to every customer's balance / free play. Not stored in the template — it won't re-apply next time.
                      </p>
                      {canAdjustBalance && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '0.9rem' }}>
                          <div>
                            <label style={labelStyle}>Credit Balance Amount</label>
                            <input type="number" step="0.01" placeholder="e.g. 25 or -10" style={inputStyle}
                              value={adj.balance_adjustment} onChange={e => setAdj(a => ({ ...a, balance_adjustment: e.target.value }))} />
                          </div>
                          <div>
                            <label style={labelStyle}>Reason (optional)</label>
                            <input style={inputStyle} value={adj.balance_reason} onChange={e => setAdj(a => ({ ...a, balance_reason: e.target.value }))} />
                          </div>
                        </div>
                      )}
                      {canAdjustFreePlay && (
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                          <div>
                            <label style={labelStyle}>Free Play Amount</label>
                            <input type="number" step="0.01" placeholder="e.g. 25" style={inputStyle}
                              value={adj.free_play_adjustment} onChange={e => setAdj(a => ({ ...a, free_play_adjustment: e.target.value }))} />
                          </div>
                          <div>
                            <label style={labelStyle}>Reason (optional)</label>
                            <input style={inputStyle} value={adj.free_play_reason} onChange={e => setAdj(a => ({ ...a, free_play_reason: e.target.value }))} />
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <button onClick={handleSave} disabled={saving} style={saveButtonStyle}>
                  {saving ? 'Saving...' : 'Save Template'}
                </button>
                <button onClick={handleApply} disabled={applying || targetCount === 0} style={applyButtonStyle}>
                  {applying ? 'Applying...' : `Apply to All ${targetLabel} (${targetCount})`}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
